import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core'
import {
  CategoryScale,
  Chart,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js'
import { map, Observable, tap } from 'rxjs'
import { ClientScore } from 'src/app/models/client/client-score-dto'
import { FlipScores } from 'src/app/models/flip/flip-score-dto'
import { ClientService } from 'src/app/services/client/client.service'
import { FlipHeaderInfo } from '../../../models/flip/flip-header-info'
import { FlipGraphScores } from '../../../models/flip/flip-graph-scores'
import { FlipGraphSubScores } from '../../../models/flip/flip-graph-sub-scores'
import { DetailsAndScoreResponse } from '../../../models/flip/details-scores-response'
import { CommonService } from '../../../services/common/common.service'

Chart.register(
  LineController,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
)

@Component({
  selector: 'app-flip-overview-graph',
  templateUrl: './flip-overview-graph.component.html',
  styleUrls: ['./flip-overview-graph.component.scss'],
  standalone: false,
})
export class FlipOverviewGraphComponent implements AfterViewInit, OnDestroy {
  @Input() clientId!: string
  @Input() detailsAndScore!: DetailsAndScoreResponse
  @ViewChild('flipGraph') skillsChart: ElementRef<HTMLCanvasElement>
  clientScore$: Observable<ClientScore[]>
  flipScore$: Observable<FlipScores[]>
  private chart: Chart

  constructor(
    private cdr: ChangeDetectorRef,
    private clientService: ClientService,
    private commonService: CommonService,
  ) {}

  // Recreate chart using existing detailsAndScore
  private refreshChart(): void {
    if (this.chart) {
      this.chart.destroy()
    }
    const clientScores =
      this.detailsAndScore?.flipGraphDatas?.map(
        (item) => new ClientScore(item),
      ) ?? []
    const flipScores =
      this.detailsAndScore?.flipUpperAndLowerLimits?.map(
        (item) => new FlipScores(item),
      ) ?? []
    // If data not loaded yet, skip to avoid errors
    if (
      !this.skillsChart?.nativeElement ||
      (!clientScores.length && !flipScores.length)
    ) {
      return
    }
    this.createChart(clientScores, flipScores)
  }

  @HostListener('window:resize')
  onResize(): void {
    // Debounce optional; for simplicity, refresh immediately
    this.refreshChart()
  }

  ngAfterViewInit() {
    this.initChartInfo()
  }

  initChartInfo() {
    if (!this.detailsAndScore) {
      this.clientService
        .DetailsAndScore(this.clientId)
        .pipe(
          tap(
            (response: {
              clientScore: ClientScore[]
              flipScores: FlipScores[]
              flipHeaderInfo: FlipHeaderInfo
              flipGraphScores: FlipGraphScores[]
              flipGraphSubScores: FlipGraphSubScores[]
            }) => console.log('Service response:', response),
          ),
          map(
            (response: {
              clientScore: ClientScore[]
              flipScores: FlipScores[]
              flipHeaderInfo: FlipHeaderInfo
              flipGraphScores: FlipGraphScores[]
              flipGraphSubScores: FlipGraphSubScores[]
            }): DetailsAndScoreResponse => {
              if (!response) {
                console.error('Service response is undefined.')
                return {
                  flipGraphDatas: [],
                  flipUpperAndLowerLimits: [],
                  flipHeaderInfo: null,
                  flipScores: [],
                  flipSubScores: [],
                } // Provide default empty arrays
              }

              this.detailsAndScore = {
                flipGraphDatas:
                  response.clientScore?.map((item) => new ClientScore(item)) ??
                  [],
                flipUpperAndLowerLimits:
                  response.flipScores?.map((item) => new FlipScores(item)) ??
                  [],
                flipHeaderInfo: response.flipHeaderInfo ?? [],
                flipScores:
                  response.flipGraphScores?.map(
                    (item) => new FlipGraphScores(item),
                  ) ?? [],
                flipSubScores:
                  response.flipGraphSubScores?.map(
                    (item) => new FlipGraphSubScores(item),
                  ) ?? [],
              } as DetailsAndScoreResponse

              return this.detailsAndScore // Cast to DetailsAndScoreResponse
            },
          ),
          tap({
            next: (data) => {
              // Check if the expected properties exist
              if (
                !data.flipGraphDatas ||
                !data.flipUpperAndLowerLimits ||
                !data.flipHeaderInfo ||
                !data.flipSubScores
              ) {
                console.error(
                  'One of the expected properties is missing:',
                  data,
                )
              }
            },
            error: (error) =>
              console.error('Error after mapping response:', error),
          }),
        )
        .subscribe({
          next: (data) => {
            // map function to create new instances if arrays are not empty

            const clientScores =
              data.flipGraphDatas?.map((item) => new ClientScore(item)) ?? []
            const flipScores =
              data.flipUpperAndLowerLimits?.map(
                (item) => new FlipScores(item),
              ) ?? []
            this.createChart(clientScores, flipScores)
          },
          error: (error) => {
            console.error('Error in final subscription:', error)
            // If there is an error, call createChart with empty arrays
            this.createChart([], [])
          },
        })
    } else {
      const clientScores =
        this.detailsAndScore.flipGraphDatas?.map(
          (item) => new ClientScore(item),
        ) ?? []
      const flipScores =
        this.detailsAndScore.flipUpperAndLowerLimits?.map(
          (item) => new FlipScores(item),
        ) ?? []
      this.createChart(clientScores, flipScores)
    }
  }

  createChart(clientScores: any[], flipScores: any[]) {
    if (this.chart) {
      this.chart.destroy()
    }

    const context = this.skillsChart.nativeElement.getContext('2d')

    // Compute max ages
    const maxClientAge =
      clientScores.length > 0
        ? Math.max(...clientScores.map((s) => s.ageinMonths))
        : 0
    const maxFlipAge =
      flipScores.length > 0
        ? Math.max(...flipScores.map((s) => s.ageinMonths))
        : 0
    const finalMaxAge = Math.max(maxClientAge, maxFlipAge)

    // Sort flipScores by ageInMonths
    const sortedFlipScores = [...flipScores].sort(
      (a, b) => a.ageinMonths - b.ageinMonths,
    )
    // Generate upperLimits
    let upperLimits = sortedFlipScores.map((score) => ({
      x: parseFloat((score.ageinMonths / 12).toFixed(2)),
      y: score.tscUpperLimit,
    }))

    // Dynamically generate labels based on finalMaxAge (in years, rounded up)
    const maxLabel = Math.ceil(finalMaxAge / 12)
    const labels = Array.from({ length: maxLabel + 1 }, (_, i) => i)

    const lowerLimits = flipScores?.map((score) => ({
      x: parseFloat((score.ageinMonths / 12).toFixed(2)),
      y: score.tscLowerLimit,
    }))
    clientScores.sort((a, b) => a.ageinMonths - b.ageinMonths)
    const professionalScores = clientScores?.map((score) => ({
      x: score.ageinMonths / 12,
      y: score?.totalScoreProfessional,
    }))
    const comparisonScores = clientScores?.map((score) => ({
      x: score.ageinMonths / 12,
      y: score?.totalScoreComparision,
    }))
    professionalScores.every((score) => score.y === 0)
    const processedProfessionalScores = this.preprocessData(professionalScores)

    const trendColour = 'rgba(0, 144, 152, 1)'
    const adaptedScoreColour = 'rgba(250, 166, 52, 1)'
    const scoreColour = 'rgba(36, 51, 131, 1)'

    const hasAdaptedScore = comparisonScores.some((score) => score.y !== null)

    // Now checking if there's any data to plot
    const hasValidData: boolean = processedProfessionalScores.length > 0
    const datasets = [
      {
        label: this.commonService.translateSafe('Expected Range Upper'),
        data: upperLimits,
        borderColor: trendColour,
        backgroundColor: trendColour,
        fill: false,
        borderDash: [5, 5],
        pointRadius: 0, // Smaller points
        borderWidth: 2,
      },
      ...(hasAdaptedScore
        ? [
            {
              label: this.commonService.translateSafe('Adapted Score'),
              data: comparisonScores,
              borderColor: adaptedScoreColour,
              backgroundColor: adaptedScoreColour,
              fill: false,
            },
          ]
        : []),
      {
        label: this.commonService.translateSafe('Expected Range Lower'),
        data: lowerLimits,
        borderColor: trendColour,
        backgroundColor: trendColour,
        fill: false,
        borderDash: [5, 5],
        pointRadius: 0, // Smaller points
        borderWidth: 2,
      },
    ]

    if (hasValidData) {
      datasets.splice(1, 0, {
        label: this.commonService.translateSafe('Professional Score'),
        data: processedProfessionalScores,
        borderColor: scoreColour,
        backgroundColor: scoreColour,
        fill: false,
      })
    }
    this.chart = new Chart(context, {
      type: 'line',
      data: {
        labels: labels,
        datasets: datasets,
      },
      options: {
        animation: false,
        responsive: true,
        maintainAspectRatio: false,
        elements: {
          line: {
            borderJoinStyle: 'round',
          },
        },
        plugins: {
          legend: {
            position: 'top', // legend position
            // This will use the point style for the legend symbols
            labels: {
              usePointStyle: true,
              pointStyle: 'circle', //  'circle' for  circular points
            },
          },
          tooltip: {
            enabled: false,
            external: (context) => {
              const { chart, tooltip } = context

              // Create or reuse tooltip element appended to document.body
              let tooltipEl = document.getElementById(
                'chartjs-flip-tooltip',
              ) as HTMLDivElement | null
              if (!tooltipEl) {
                tooltipEl = document.createElement('div')
                tooltipEl.id = 'chartjs-flip-tooltip'
                tooltipEl.style.pointerEvents = 'none'
                tooltipEl.style.position = 'absolute'
                tooltipEl.style.transition = 'all .08s ease'
                tooltipEl.style.background = 'white'
                tooltipEl.style.border = '1px solid rgba(0,0,0,0.08)'
                tooltipEl.style.boxShadow = '0 2px 6px rgba(0,0,0,0.08)'
                tooltipEl.style.padding = '8px 10px'
                tooltipEl.style.borderRadius = '4px'
                tooltipEl.style.fontSize = '12px'
                tooltipEl.style.color = '#222'
                tooltipEl.style.zIndex = '10000'
                tooltipEl.style.minWidth = '180px'
                tooltipEl.style.maxWidth = '320px'
                document.body.appendChild(tooltipEl)
              }

              // Hide when tooltip not visible
              if (!tooltip || !tooltip.opacity || tooltip.opacity === 0) {
                tooltipEl.style.opacity = '0'
                tooltipEl.style.pointerEvents = 'none'
                return
              }

              // Use first data point
              const dataPoint = tooltip.dataPoints?.[0]
              const dataIndex = dataPoint ? dataPoint.dataIndex : undefined
              const hovered = tooltip.dataPoints?.[0]
              const datasetIndex = hovered ? hovered.datasetIndex : undefined

              const clientScore =
                dataIndex !== undefined ? clientScores[dataIndex] : undefined
              const flipScore = this.getClosestFlipScore(
                flipScores,
                clientScore,
                dataIndex ?? 0,
              )

              // Build rows (kept minimal — adapt as needed)
              // Hovered value: prefer parsed.y, handle raw number/object, format 2 decimals
              const hoveredValue = (() => {
                const h = hovered as any
                let num: number | null = null
                if (h?.parsed?.y !== undefined) num = Number(h.parsed.y)
                else if (typeof h?.raw === 'number') num = Number(h.raw)
                else if (h?.raw?.y !== undefined) num = Number(h.raw.y)
                if (num === null || Number.isNaN(num)) return ''
                return num.toFixed(2)
              })()

              // Get dataset colors for legend
              const getDatasetColor = (index: number) => {
                const dataset = chart.data.datasets[index]
                return (
                  dataset?.borderColor || dataset?.backgroundColor || '#000'
                )
              }

              let sixAndAbove = flipScore?.ageinMonths >= 72

              const rows = [
                hoveredValue
                  ? {
                      label: hoveredValue,
                      value: '',
                      color: null,
                    }
                  : null,
                ...(!sixAndAbove
                  ? [
                      {
                        label: this.commonService.translateSafe(
                          'Expected Range (Upper)',
                        ),
                        value: flipScore?.tscUpperLimit ?? '',
                        color: trendColour,
                      },
                    ]
                  : []),
                {
                  label: this.commonService.translateSafe('Professional Score'),
                  value: clientScore?.totalScoreProfessional ?? '',
                  color: scoreColour,
                },
                ...(!sixAndAbove
                  ? [
                      {
                        label: this.commonService.translateSafe(
                          'Expected Range (Lower)',
                        ),
                        value: flipScore?.tscLowerLimit ?? '',
                        color: trendColour,
                      },
                    ]
                  : []),
                {
                  label: this.commonService.translateSafe(
                    'Age at Assessment (approx.)',
                  ),
                  value: clientScore?.ageString ?? '',
                  color: 'transparent',
                },
              ].filter(Boolean) as {
                label: string
                value?: string | number
                color: string
              }[]

              tooltipEl.innerHTML = rows
                .map(
                  (r) => `
                  <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin:2px 0">
                    <div style="display:flex;align-items:center;flex:1;min-width:0">
                      <span style="display:inline-block;width:10px;height:10px;margin-right:8px;flex:0 0 10px;background-color:${
                        r.color
                      };border-radius:50%;"></span>
                      <div style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${
                        r.label
                      }</div>
                    </div>
                    <div style="flex:0 0 auto;margin-left:12px;color:#333">${
                      r.value ?? ''
                    }</div>
                  </div>`,
                )
                .join('')

              tooltipEl.style.opacity = '1'
              tooltipEl.style.pointerEvents = 'none'

              // Positioning: use canvas-relative coordinates
              const canvasRect = chart.canvas.getBoundingClientRect()

              // Get actual canvas position values (already in CSS pixels)
              const caretX = tooltip.caretX ?? dataPoint?.element?.x ?? 0
              const caretY = tooltip.caretY ?? dataPoint?.element?.y ?? 0

              // Calculate viewport position
              const left = canvasRect.left + window.scrollX + caretX
              const top = canvasRect.top + window.scrollY + caretY

              // Position tooltip above the cursor with an offset
              const offset = 10
              tooltipEl.style.left = `${Math.round(left)}px`

              // Temporarily set top to measure height
              tooltipEl.style.top = `${Math.round(top)}px`
              const tooltipHeight = tooltipEl.offsetHeight

              // Position above the cursor
              const finalTop = top - tooltipHeight - offset
              tooltipEl.style.top = `${Math.round(finalTop)}px`
            },
          },
        },
        scales: {
          y: {
            type: 'linear',
            beginAtZero: true,
            title: {
              display: true,
              text: this.commonService.translateSafe('Total items scored'),
            },
            ticks: {
              callback: function (value, index, values) {
                return value
              },
            },
          },
          x: {
            type: 'linear',
            min: 0,
            max: labels.length > 0 ? labels[labels.length - 1] : 6,
            title: {
              display: true,
              text: this.commonService.translateSafe('Age in years'),
            },
            ticks: {
              callback: function (value, index, values) {
                return Number.isInteger(value) ? value : ''
              },
            },
          },
        },
      },
    })
  }

  preprocessData(scores: any) {
    let startIndex = scores.findIndex((score: { y: number }) => score.y >= 0)
    return startIndex !== -1 ? scores.slice(startIndex) : []
  }

  ngOnDestroy(): void {
    this.chart?.destroy()
  }

  getClosestFlipScore(
    flipScores: any[],
    clientScore: ClientScore,
    index: number,
  ): FlipScores {
    let flipScoreClosest = undefined
    if (clientScore && flipScores?.length) {
      const target = clientScore.ageinMonths
      flipScoreClosest = flipScores.reduce((prev, curr) =>
        Math.abs(curr.ageinMonths - target) <
        Math.abs(prev.ageinMonths - target)
          ? curr
          : prev,
      )
    } else {
      // Fallback: try match by same index or first element
      flipScoreClosest =
        (flipScores && flipScores[index]) ||
        flipScores[index] ||
        flipScores?.[0]
    }

    return flipScoreClosest
  }
}
